import { useState } from 'react';

import { api } from '../api';
import {
  NEO_NAMES_UPDATED_EVENT,
  labelConfirmErrorMessage,
  parseLabelChanges,
  type NeoLabelChange,
  type NeoLabelChangeRequest,
} from '../lib/neoNames';

export function NeoLabelChangeCard({
  request,
  onConfirmed,
}: {
  request: NeoLabelChangeRequest;
  onConfirmed: (change: NeoLabelChange) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kept, setKept] = useState(request.status === 'dismissed');
  const [done, setDone] = useState(request.status === 'applied');

  async function confirm() {
    if (busy || done) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ change: unknown }>('/v1/neo/label-changes/confirm', {
        method: 'POST',
        body: { requestId: request.requestId },
        idempotencyKey: request.requestId,
      });
      const change = parseLabelChanges([result.change], request.boatId)[0];
      if (change === undefined) throw new Error('unreadable name result');
      setDone(true);
      onConfirmed(change);
      window.dispatchEvent(
        new CustomEvent(NEO_NAMES_UPDATED_EVENT, { detail: { boatId: change.boatId } })
      );
    } catch (error) {
      setError(labelConfirmErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function dismiss() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/neo/label-changes/${request.requestId}/dismiss`, { method: 'POST' });
      setKept(true);
    } catch (error) {
      setError(labelConfirmErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  if (kept) return <p className="neo-proposal-dismissed">Name left as it is.</p>;
  return (
    <article className="neo-proposal" data-testid="neo-label-change">
      <p className="neo-proposal__eyebrow">
        {done ? 'Name changed' : 'Shared name — needs your confirmation'}
      </p>
      <h3 className="neo-proposal__title">
        {request.beforeLabel} → {request.label}
      </h3>
      <p>
        Every owner on this boat will see this name on boards, in pickers and in future Neo answers.
      </p>
      <p>Custom board labels stay as you set them.</p>
      {done ? (
        <p role="status">Renamed everywhere. Undo is below.</p>
      ) : (
        <>
          <p>Nothing changes until you confirm. The device’s own settings stay as they are.</p>
          {error !== null ? (
            <p className="neo-proposal__error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="neo-proposal__actions">
            <button
              type="button"
              className="neo-proposal__create"
              disabled={busy}
              onClick={() => void confirm()}
            >
              {busy ? 'Applying…' : 'Rename everywhere'}
            </button>
            <button
              type="button"
              className="neo-inline-button"
              disabled={busy}
              onClick={() => void dismiss()}
            >
              Keep current name
            </button>
          </div>
        </>
      )}
    </article>
  );
}
